// /src/pages/private/members/personalhub/hangar/cards/VehicleListRow.jsx
// input-security: verified — fields: ship-label-input (userLabel draft)

import React, { useEffect, useRef, useState } from 'react';
import { sanitizePersistedUserTextDraft } from '../../../../../../utils/sanitizeUserTextInput';
import VehicleOverlayPanel from './VehicleOverlayPanel';
import { normalizeHangarSize } from '../normalizeHangarSize';

const LABEL_ELIGIBLE_SIZES = new Set(['medium', 'large', 'capital']);
const SHIP_LABEL_PLACEHOLDER = 'Name this ship';

function sanitizeShipLabelDraft(value) {
  return sanitizePersistedUserTextDraft(value);
}

function prodStateClass(prodState) {
  return prodState?.toLowerCase().replace(/\s+/g, '-') || '';
}

function ShipLabelEditor({
  draft,
  saving,
  inputRef,
  labelError,
  onDraftChange,
  onKeyDown,
  onBlur,
}) {
  return (
    <div className="ship-label-editor" onClick={(event) => event.stopPropagation()}>
      <span className="ship-label-icon" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        className={`ship-label-input ${labelError ? 'ship-label-input--error' : ''}`}
        placeholder={SHIP_LABEL_PLACEHOLDER}
        value={draft}
        maxLength={255}
        disabled={saving}
        onChange={onDraftChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        aria-invalid={labelError ? 'true' : 'false'}
        data-resource="private.navbar.personalhub.hangar.ship-label"
        data-action="write"
        data-route="/api/user/update-hangar-ship-label"
      />
      {labelError && (
        <span className="ship-label-error" role="alert">
          {labelError}
        </span>
      )}
    </div>
  );
}

function ShipLabelTrigger({ canEdit, displayName, onStartEdit }) {
  const showPlaceholderHint = canEdit && !displayName;
  const labelText = displayName || (showPlaceholderHint ? SHIP_LABEL_PLACEHOLDER : '-');

  return (
    <button
      type="button"
      className={`ship-label-trigger ${canEdit ? 'ship-label-trigger--editable' : 'ship-label-trigger--readonly'}`}
      onClick={(event) => {
        event.stopPropagation();
        onStartEdit();
      }}
      disabled={!canEdit}
      data-resource={canEdit ? 'private.navbar.personalhub.hangar.ship-label' : undefined}
      data-action={canEdit ? 'write' : undefined}
      data-route={canEdit ? '/api/user/update-hangar-ship-label' : undefined}
    >
      <span className="ship-label-icon" aria-hidden="true" />
      <span className={`ship-label-text ${showPlaceholderHint ? 'ship-label-text--placeholder' : ''}`}>
        {labelText}
      </span>
    </button>
  );
}

export default function VehicleListRow({
  v,
  isLoaner = false,
  onSaveLabel,
  canRemoveIngame = false,
  canEditLabel = false,
}) {
  const source = v.sourceVehicle;
  const isManual = v.inGame === true;
  const hasRsiName = Boolean(v.customName);
  const hasUserLabel = Boolean(v.userLabel);
  const sizeKey = normalizeHangarSize(v.size);
  const isLabelEligible = LABEL_ELIGIBLE_SIZES.has(sizeKey);
  const displayName = hasRsiName ? v.customName : (v.userLabel || null);
  const showShipLabel = !isLoaner && (hasRsiName || isLabelEligible);
  const canEdit = !isLoaner
    && !hasRsiName
    && isLabelEligible
    && canEditLabel
    && typeof onSaveLabel === 'function';
  const labelTone = hasRsiName ? 'rsi' : (hasUserLabel ? 'user' : 'empty');

  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [labelError, setLabelError] = useState('');
  const inputRef = useRef(null);
  const skipBlurCancelRef = useRef(false);
  const labelErrorRef = useRef('');

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const setShipLabelError = (message) => {
    labelErrorRef.current = message;
    setLabelError(message);
  };

  const startEdit = () => {
    if (!canEdit || saving) return;
    setDraft(v.userLabel || '');
    setShipLabelError('');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setDraft('');
    setShipLabelError('');
  };

  const commitEdit = async () => {
    if (!canEdit || saving) return;

    skipBlurCancelRef.current = true;
    setSaving(true);
    setShipLabelError('');
    try {
      await onSaveLabel({
        handleId: v.handleId,
        vehicleId: v.id,
        packageName: v.packageName || null,
        ingameId: isManual ? v.ingameId : null,
        userLabel: draft,
      });
      setIsEditing(false);
      setDraft('');
    } catch (error) {
      setShipLabelError(error?.message || 'Failed to save ship label');
    } finally {
      setSaving(false);
      skipBlurCancelRef.current = false;
    }
  };

  const handleLabelBlur = () => {
    window.setTimeout(() => {
      if (!skipBlurCancelRef.current && !labelErrorRef.current) {
        cancelEdit();
      }
    }, 0);
  };

  const handleLabelKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitEdit();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  const toggleExpanded = () => {
    if (isEditing) return;
    setExpanded((prev) => !prev);
  };

  return (
    <div
      className={[
        'vehicle-list-row',
        `size-${sizeKey}`,
        isLoaner ? 'loaner' : '',
        expanded ? 'vehicle-list-row--expanded' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="vehicle-list-row__header"
        onClick={toggleExpanded}
        aria-expanded={expanded}
      >
        <div className="vehicle-list-row__thumb">
          {v.mainImageUrl ? (
            <img src={v.mainImageUrl} alt="" loading="lazy" draggable="false" />
          ) : (
            <span className="vehicle-list-row__thumb-placeholder" aria-hidden="true" />
          )}
        </div>

        <div className="vehicle-list-row__title vehicle-title">
          <span>{v.vehicleName}</span>
          {isLoaner && source && (
            <div className="loaner-source">Loaner for the {source.vehicleName}</div>
          )}
        </div>

        <div className="vehicle-list-row__label">
          {showShipLabel ? (
            <div className={`ship-label-row ship-label-row--${labelTone}`}>
              {canEdit && isEditing ? (
                <ShipLabelEditor
                  draft={draft}
                  saving={saving}
                  inputRef={inputRef}
                  labelError={labelError}
                  onDraftChange={(event) => {
                    setShipLabelError('');
                    setDraft(sanitizeShipLabelDraft(event.target.value));
                  }}
                  onKeyDown={handleLabelKeyDown}
                  onBlur={handleLabelBlur}
                />
              ) : (
                <ShipLabelTrigger
                  canEdit={canEdit}
                  displayName={displayName}
                  onStartEdit={startEdit}
                />
              )}
            </div>
          ) : (
            <span className="vehicle-list-row__label-empty">-</span>
          )}
        </div>

        <div className="vehicle-list-row__manufacturer vehicle-manufacturer">{v.manufacturer}</div>

        <div className="vehicle-list-row__actions">
          <div className="vehicle-list-row__remove-slot">
            {isManual ? (
              <button
                type="button"
                className="manual-remove-btn"
                disabled={!canRemoveIngame}
                onClick={(event) => {
                  event.stopPropagation();
                  v.onRemove && v.onRemove(v.ingameId);
                }}
                data-resource="private.navbar.personalhub.hangar.ingame.remove"
                data-action="delete"
                data-route="/api/user/remove-in-game-ship"
              >
                remove
              </button>
            ) : (
              <span className="vehicle-list-row__remove-placeholder" aria-hidden="true" />
            )}
          </div>
        </div>

        <div className="vehicle-list-row__prod">
          <span className={`prod-state ${prodStateClass(v.prodState)}`}>
            {v.prodState}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="vehicle-list-row__accordion">
          <VehicleOverlayPanel vehicle={v} isLoaner={isLoaner} compact />
        </div>
      )}
    </div>
  );
}
